import { I18n } from "react-redux-i18n";
import auth, { featureEnabled } from "../auth";
import { getPaymentMethod } from "../tableMatePaid";
import moment from "moment";

const TAB = "\t";

const encodeCategoryNameMax50 = (name) => {
    let value = (name || "").trim();
    let encoded = encodeURIComponent(value);

    while (encoded.length > 35 && value.length > 0) {
        value = value.slice(0, -1);
        encoded = encodeURIComponent(value);
    }
    console.log(encoded.length, value.length, encoded);
    return encoded;
};

// Returning goods (e.g. bottles) and paying the money back is only possible with a storno that refers to a receipt which does not exist.
// These numbers must never match a real receipt (Z count 9999, receipt 99999).
const FALSE_STORNO_Z_COUNT = 1;
const FALSE_STORNO_RECEIPT_NO = 1;

// True when there is at least one non-zero line and every non-zero line is negative.
const hasOnlyNegativeAmounts = (amounts) => {
    const lines = Object.values(amounts || {}).filter(category => category.amount || category.service_amount);
    return lines.length > 0 && lines.every(category => !(category.amount > 0) && !(category.service_amount > 0));
};

// bbar (return custom fiscal receipt) that refers to a receipt which does not exist, followed by the returned items (sbt) and a crr that pays the money back.
// bbar: APNumber, Date, ReceiptType, Z1Count, RecNo, Reason (3 = other), then 9 empty fields (taxRefNum, customerName, postCode, city,
// address, street type, house number, Comment, PaidMode). The payment is sent in crr: crr<TAB>1<TAB>PaidMode[,Amount][|...]; a PaidMode without
// amount (the last one) pays the remainder.
const buildFalseStornoReceipt = (amounts, types) => {
    const payments = (types || []).filter(t => Number(t.amount) < 0);
    if (payments.length === 0) {
        return "";
    }

    const ap = "Y12345678";
    const toPositive = (value) => String(Math.abs(Number(value))).replace(",", ".");
    const vatCodes = { 5: "A", 18: "B", 27: "C", s5: "A", s18: "B", s27: "C", undefined: "E", null: "E", 0: "E" };
    const paymentCodes = { "cash": "P", "card": "N", "hellopay": "N", "online": "N", "teya": "N", "bizpay": "N", "paynance": "N", "utalvany": "O3" };

    const paidModes = payments.map((t, index) => {
        const paymentMethod = getPaymentMethod(t.type);
        const paymentType = paymentMethod?.method?.type ?? paymentMethod?.type ?? t.type;
        const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
        const isEurCash = t.currency === "EUR" && normalizedPaymentType === "cash";
        const code = isEurCash ? "F0" : (paymentCodes[normalizedPaymentType] || "N");
        // The last paid mode has no amount, it pays the remainder.
        if (index === payments.length - 1) {
            return code;
        }
        return code + "," + (isEurCash ? Math.abs(Math.round(100 * t.amount) / 100) : toPositive(t.amount));
    });

    let url = "command=bbar" + TAB + [
        ap,
        moment().format("YYMMDD"),
        "F",
        FALSE_STORNO_Z_COUNT,
        FALSE_STORNO_RECEIPT_NO,
        "3"
    ].concat(new Array(9).fill("")).join(TAB) + "\r\n";

    const services = {};
    Object.values(amounts).sort((a, b) => a.entryOrder - b.entryOrder).filter(category => category.amount || category.service_amount).forEach(category => {
        const count = Math.abs(Number(category.count)) || 1;
        if (category.amount) {
            url += "Command=sbt" + TAB + TAB + encodeCategoryNameMax50(category.name) + TAB + TAB + TAB + TAB + TAB + vatCodes[category.vat] + TAB + "00" + TAB + toPositive(category.amount) + TAB + count + TAB + "0" + "\r\n";
        }
        if (category.service_amount) {
            services[category.vat] = (services[category.vat] || 0) + Math.abs(category.service_amount) * count;
        }
    });

    Object.keys(services).forEach(key => {
        if (vatCodes["s" + key]) {
            url += "Command=sbt" + TAB + TAB + (services[key] > 2 ? I18n.t("admin_local.fiscat_service_fee") + " (" + I18n.t("admin_local.vat") + " " + key + "%)" : I18n.t("admin_local.rounding")) + TAB + TAB + TAB + TAB + TAB + vatCodes["s" + key] + TAB + "00" + TAB + toPositive(services[key]) + TAB + 1 + TAB + "0" + "\r\n";
        }
    });

    // RefundParam 1 = recycle payment, then PaidMode,Amount pairs separated by |
    url += "Command=crr" + TAB + "1" + TAB + paidModes.join("|") + "\r\n";
    return url;
};

export const generate_a234 = (def, amounts, cashierType, payable, types, amountPaidExchangeRate, serviceVat, tip, type = "cash", tableMate, seqnr) => {
    if (hasOnlyNegativeAmounts(amounts)) {
        const stornoUrl = buildFalseStornoReceipt(amounts, types);
        console.log(stornoUrl);
        def.resolve(stornoUrl);
        return;
    }

    var url = "";
    var a234 = {
        5: "A",
        18: "B",
        27: "C",
        s5: "A",
        s18: "B",
        s27: "C",
        undefined: "E",
        null: "E",
        0: "E",
    };

    var services = {};
    var count = 1;

    var totalAmount = 0;

    url = "";

    const ClerkID = "1", Password = "0", ExternalPower = "0", InvoiceType = "";

    url += "Command=ofr" + TAB + ClerkID + TAB + Password + TAB + ExternalPower + TAB + InvoiceType + "\r\n";

    if (seqnr)
        url += "Command=pcd" + TAB + "T" + TAB + 0xFF + "\r" + I18n.t("admin_local.order_sequence_number") + " #" + seqnr + "\r\n";

    Object.values(amounts).sort((a, b) => a.entryOrder - b.entryOrder).filter(category => category.amount + category.service_amount).forEach(category => {
        if (category.amount) {
            //eslint-disable-next-line no-useless-concat
            url += "Command=sbt" + TAB + TAB + encodeCategoryNameMax50(category.name) + TAB + TAB + TAB + TAB + TAB + a234[category.vat] + TAB + "00" + TAB + (category.amount) + TAB + category.count + TAB + "0,0" + "\r\n";
        }

        totalAmount += category.amount;
        count++;
        if (category.service_amount > 0) {
            if (!services[category.vat]) {
                services[category.vat] = 0;
            }
            services[category.vat] += category.service_amount * category.count;
            totalAmount += category.service_amount * category.count;
        }
    });

    /*
    if (Object.keys(services).length && Math.abs(payable - totalAmount) < 5) {
        services[Object.keys(services)[0]] -= totalAmount - payable;
    }*/

    var serviceUrl = "";

    Object.keys(services).forEach(key => {
        if (a234["s" + key])
            url += "Command=sbt" + TAB + TAB + (services[key] > 2 ? I18n.t("admin_local.fiscat_service_fee") + " (" + I18n.t("admin_local.vat") + " " + key + "%)" : I18n.t("admin_local.rounding")) + TAB + TAB + TAB + TAB + TAB + a234["s" + key] + TAB + "00" + TAB + ("" + auth.round(services[key])).replace(",", ".") + TAB + 1 + TAB + "0,0" + "\r\n";
        count++;
    });

    //url += "Command=pcd" + TAB + "C" + TAB + "1,690123456789" + "\r\n";
    if (featureEnabled("history/feedback") && tableMate?.id) {
        url += "Command=pcd" + TAB + "T" + TAB + 0xFF + "\r" + I18n.t("admin_local.scanit") + "\r\n";
        url += "Command=pcd" + TAB + "T" + TAB + 0xFF + "\r" + I18n.t("admin_local.rateus") + "\r\n";
        url += "Command=pcd" + TAB + "C" + TAB + encodeURIComponent("q,https://restaurant.eatwithme.online" + (auth.myStatus.test_mode ? ":8444" : "") + "/feedback.html?r=" + auth.myStatus.restaurant_global_id + "&t=" + sessionStorage.tableNumberSelected + "&m=" + tableMate.id + "&q=" + tableMate.qrToken + "&p=TABLEMATEPAYMENTID") + "\r\n";
    } else {
        const isShopMode = auth?.myStatus?.restaurant_settings?.["shop-mode"];
        if (isShopMode) {
            url += "Command=pcd" + TAB + "C" + TAB + "2,TABLEMATEPAYMENTID0" + "\r\n";
        } else {
            const scanABill = featureEnabled("history/scan-a-bill");
            if (scanABill && tableMate) {
                console.log("https://restaurant.eatwithme.online" + (auth.myStatus.test_mode ? ":8444" : "") + "/map.html?r=" + auth.myStatus.restaurant_global_id + "&t=" + sessionStorage.tableNumberSelected + "&m=" + tableMate.id + "&q=" + tableMate.qrToken + "&p=true&c=true");
                url += "Command=pcd" + TAB + "C" + TAB + encodeURIComponent("q,https://restaurant.eatwithme.online" + (auth.myStatus.test_mode ? ":8444" : "") + "/map.html?r=" + auth.myStatus.restaurant_global_id + "&t=" + sessionStorage.tableNumberSelected + "&m=" + tableMate.id + "&q=" + tableMate.qrToken + "&p=true&c=true&a=m") + "\r\n";
            }
        }
    }

    //url += "Command=pcd" + TAB + "C" + TAB + "1%2C" + "TABLEMATEPAYMENTID" + "\r\n";

    var paymentMethods = {
        "cash": "P",
        "card": "N",
        "hellopay": "N",
        "online": "N",
        "teya": "N",
        "bizpay": "N",
        "paynance": "N",
        "utalvany": "O3"
    }

    types.filter(t => Number(t.amount) > 0).forEach((t, ind) => {
        //if (ind > 0)
        //  url += "|";
        const paymentMethod = getPaymentMethod(t.type);
        const paymentType = paymentMethod?.method?.type ?? paymentMethod?.type ?? t.type;
        const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
        const paymentCode = paymentMethods[normalizedPaymentType] || "N";

        var roundFunction = auth.myStatus.restaurant_base_currency["javaScriptRoundFunction"];
        var amount = t.amount;
        // eslint-disable-next-line no-eval
        //amount = eval(roundFunction.replace("amount", t.amount));

        if (t.currency === "EUR" && normalizedPaymentType === "cash")
            url += "Command=pmt" + TAB + TAB + TAB + "F0" + TAB + Math.round(100 * t.amount) / 100 + "\r\n";
        else {
            url += "Command=pmt" + TAB + TAB + TAB + paymentCode + TAB + amount + "\r\n";
        }
    });


    url += "Command=cfr" + TAB + "\r\n";
    /*
    url += "Command=" + "onfr\r\n";
    url += "Command=" + "pnft" + TAB + 0x80 + "\r" + "jhkjhkjhkh" + "\n";
    url += "Command=" + "cnfr\r\n";
    */

    if (types.filter(t => t.amount > 0).length === 0) {
        url = "";
    }

    if (tip > 0) {
        if (type === "cash")
            url += "Command=" + "ra" + TAB + "6" + TAB + TAB + "P," + (Math.floor(tip / 5) * 5) + "|" + "COPY1" + "\r\n";
        else
            url += "Command=" + "ra" + TAB + "6" + TAB + TAB + "N," + tip + "|" + "COPY1" + "\r\n";
    }



    console.log(url);
    def.resolve(url);
}


export const dayOpen_a234 = (amount = 0) => {
    return { text: "command=fod" + TAB + "R\r\ncommand=fod" + TAB + "P," + amount, pattern: ".*" };
}

// Read status flags from the Status field.
// Byte2 bit6 set means the day is open.
// Byte0 bit6 together with Byte2 bit6 means the day was left open too long and must be closed/opened again.
// Byte1 bit5 set means the printer cover is open.
// Byte2 bit0 set means the printer is out of paper.
// Status format is Byte0-Byte1-Byte2-Byte3-Byte4-Byte5 in hex (e.g. 80-80-80-80-80-94).
export const parseCashRegisterStatus_a234 = (status) => {
    if (typeof status !== "string") {
        return { open: false, needsCloseOpen: false, coverOpen: false, outOfPaper: false };
    }

    const bytes = status.split("-");
    if (bytes.length < 6) {
        return { open: false, needsCloseOpen: false, coverOpen: false, outOfPaper: false };
    }

    const byte0 = parseInt(bytes[0], 16);
    const byte1 = parseInt(bytes[1], 16);
    const byte2 = parseInt(bytes[2], 16);
    if (Number.isNaN(byte0) || Number.isNaN(byte1) || Number.isNaN(byte2)) {
        return { open: false, needsCloseOpen: false, coverOpen: false, outOfPaper: false };
    }

    const byte0Bit6 = (byte0 & 0x40) !== 0;
    const byte1Bit5 = (byte1 & 0x20) !== 0;
    const byte2Bit6 = (byte2 & 0x40) !== 0;
    const byte2Bit0 = (byte2 & 0x01) !== 0;

    return {
        open: byte2Bit6 && !byte0Bit6,
        needsCloseOpen: byte0Bit6 && byte2Bit6,
        coverOpen: byte1Bit5,
        outOfPaper: byte2Bit0,
        byte0Bit6,
        byte1Bit5,
        byte2Bit6,
        byte2Bit0
    };
}

export const isCashRegisterOpen_a234 = () => {
    return {
        text: `command=rs${TAB}\r\n`,
        pattern: ".*<Status>[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}-[0-9A-Fa-f]{2}</Status>.*"
    };
}

export const dayClose_a234 = () => {
    return { text: "command=pgr" + TAB + "1" + TAB + "19\r\n", pattern: ".*" };
    //return "command=pfdrd" + TAB + "220925" + TAB + "280925\r\n";
}

// Prints the electronic journal text (daily closure reports) for a period, dates are YYMMDDhhmm.
export const printDayCloseReports_a234 = (fromDate, toDate) => {
    return { text: "command=pejd" + TAB + "P,1,2,524288," + moment(fromDate).format("YYMMDD") + "0000" + moment(toDate).format("YYMMDD") + "2359" + "\r\n", pattern: ".*" };
}

// Prints a copy of a stored sale receipt from the electronic journal. receiptNo format: ZZZZ/NNNNN.
// pejd P,1,5,<receipt type>,<Z 4 digits><receipt 5 digits>; type 1 = sale receipt (format taken from a device log).
export const reprintReceipt_a234 = (receiptNo) => {
    const match = /^\s*(\d{1,4})\/(\d{1,5})\s*$/.exec(String(receiptNo || ""));
    if (!match) {
        return null;
    }
    return { text: "command=pejd" + TAB + "P,1,5,1," + match[1].padStart(4, "0") + match[2].padStart(5, "0") + "\r\n", pattern: ".*" };
}

export const storno_a234 = (data, def) => {
    def.resolve("command=sfr" + TAB + "XXXXXXXXX" + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + Number(data.receiptNumber.substring(0, 4)) + TAB + Number(data.receiptNumber.substring(5)) + TAB + data.reason + TAB + data.customerTaxNum + TAB + data.customerName + TAB + data.customerPostCode + TAB + data.customerTown + TAB + data.customerAddress + TAB + TAB + TAB + "P");
}

export const refund_a234 = (data, def, amounts, cashierType, payable, types, amountPaidExchangeRate, serviceVat, tip, type = "cash", tableMate, seqnr) => {
    var url = "";
    var a234 = {
        5: "A",
        18: "B",
        27: "C",
        s5: "A",
        s18: "B",
        s27: "C",
        undefined: "E",
        null: "E",
        0: "E",
    };

    var id = 0;

    const prefix = "";
    var services = {};
    var count = 1;

    var totalAmount = 0;

    url = "";

    const ClerkID = "1", Password = "0", ExternalPower = "0", InvoiceType = "";

    url += "command=sar" + TAB + "XXXXXXXXX" + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + Number(data.receiptNumber.substring(0, 4)) + TAB + Number(data.receiptNumber.substring(5)) + TAB + data.reason + TAB + data.customerTaxNum + TAB + data.customerName + TAB + data.customerPostCode + TAB + data.customerTown + TAB + data.customerAddress + TAB + TAB + TAB;

    var paymentMethods = {
        "cash": "P",
        "card": "N",
        "hellopay": "N",
        "online": "N",
        "teya": "N",
        "bizpay": "N",
        "paynance": "N",
        "utalvany": "O3"
    }

    types.filter(t => Number(t.amount) > 0).forEach((t, ind) => {
        //if (ind > 0)
        //  url += "|";
        const paymentMethod = getPaymentMethod(t.type);
        const paymentType = paymentMethod?.method?.type ?? paymentMethod?.type ?? t.type;
        const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
        const paymentCode = paymentMethods[normalizedPaymentType] || "N";

        var roundFunction = auth.myStatus.restaurant_base_currency["javaScriptRoundFunction"];
        var amount = t.amount;
        // eslint-disable-next-line no-eval
        //amount = eval(roundFunction.replace("amount", t.amount));

        if (t.currency === "EUR" && normalizedPaymentType === "cash")
            url += "F0" + TAB + Math.round(100 * t.amount) / 100 + "\r\n";
        else {
            url += paymentCode + TAB + amount + "\r\n";
        }
    });


    Object.values(amounts).sort((a, b) => a.entryOrder - b.entryOrder).filter(category => category.amount + category.service_amount).forEach(category => {
        if (category.amount) {
            //eslint-disable-next-line no-useless-concat
            url += "Command=sbt" + TAB + TAB + encodeCategoryNameMax50(category.name) + TAB + TAB + TAB + TAB + TAB + a234[category.vat] + TAB + "00" + TAB + (category.amount) + TAB + category.count + TAB + "0" + "\r\n";
        }

        totalAmount += category.amount;
        count++;
        if (category.service_amount > 0) {
            if (!services[category.vat]) {
                services[category.vat] = 0;
            }
            services[category.vat] += category.service_amount * category.count;
            totalAmount += category.service_amount * category.count;
        }

    });


    /*
    if (Object.keys(services).length && Math.abs(payable - totalAmount) < 5) {
        services[Object.keys(services)[0]] -= totalAmount - payable;
    }*/

    var serviceUrl = "";

    Object.keys(services).forEach(key => {
        if (a234["s" + key])
            url += "Command=sbt" + TAB + TAB + (services[key] > 2 ? I18n.t("admin_local.fiscat_service_fee") + " (" + I18n.t("admin_local.vat") + " " + key + "%)" : I18n.t("admin_local.rounding")) + TAB + TAB + TAB + TAB + TAB + a234["s" + key] + TAB + "00" + TAB + ("" + services[key]).replace(",", ".") + TAB + 1 + TAB + "0,0" + "\r\n";
        count++;
    });

    url += "Command=crr" + TAB + 0 + "\r\n";

    console.log(url);
    def.resolve(url);
}

export const cashin_a234 = (reason, amount) => {
    return "Command=" + "ra" + TAB + reason + TAB + TAB + "P," + amount + "|" + "COPY1" + "\r\n";
}
export const cashout_a234 = (reason, amount) => {
    return "Command=" + "po" + TAB + reason + TAB + TAB + "P," + amount + "|" + "COPY1" + "\r\n";
}

/**
 * Generates a non-fiscal slip for kiosk cash payment on an a234 printer.
 *
 * The slip shows the restaurant name, date/time, order sequence number, a
 * breakdown of the ordered items, the total amount payable, and a message
 * directing the guest to pay at the cashier.
 *
 * Commands use the a234 non-fiscal protocol:
 *   onfr  – open non-fiscal receipt
 *   pnft  – print non-fiscal text: pnft<TAB><format><CR><text>
 *             format 0x00 = normal text, 0x80 = centred / double-width header
 *   cnfr  – close non-fiscal receipt
 *
 * @param {jQuery.Deferred} def        Deferred resolved with the command string.
 * @param {number|string}   seqnr      Order sequence number shown on the slip.
 * @param {Object}          amounts    Category-keyed object identical to the one
 *                                     used by generate_a234 (may be null/undefined
 *                                     to omit line items).
 * @param {number}          payable    Total amount the guest must pay.
 * @param {string}          currencyName  Currency symbol/name (e.g. "HUF", "€").
 */
export const generate_nonfiscal_slip_a234 = (def, seqnr, amounts, payable, currencyName, targetTableName) => {
    const SEP = "--------------------------------";
    const FMT_NORMAL = "\x80";   // normal left-aligned text
    const FMT_CENTER = "\x9A";   // centred / double-width header

    const pnft = (fmt, text) =>
        "Command=pnft" + TAB + fmt + "\r" + text + "\r\n";

    const ClerkID = "1", Password = "0", ExternalPower = "0";

    let url = "";

    url += "Command=onfr" + TAB + ClerkID + TAB + Password + TAB + ExternalPower + "\r\n";

    url += pnft(FMT_NORMAL, SEP);

    // Sequence number — the key reference the guest brings to the cashier
    if (seqnr)
        url += pnft(FMT_CENTER, I18n.t("admin_local.order_sequence_number") + ": #" + seqnr);

    url += pnft(FMT_NORMAL, SEP);

    // Line items — one line per item matching fiscal receipt style: name  unitPrice  count
    if (amounts) {
        Object.values(amounts)
            .sort((a, b) => (a.entryOrder || 0) - (b.entryOrder || 0))
            .filter(category => category.amount)
            .forEach(category => {
                const name = (category.name || "").trim().toLowerCase();
                const count = category.count || 1;
                const total = category.amount || 0;
                const unitPrice = Math.round((total / count) * 100) / 100;
                const cur = currencyName || "";
                const priceStr = unitPrice + " " + cur;
                const countStr = "" + count;
                // truncate name to leave room for price and count
                const maxName = 31 - priceStr.length - countStr.length - 2;
                const nameStr = name.length > maxName ? name.substring(0, maxName) : name;
                const padding = Math.max(1, 31 - nameStr.length - priceStr.length - countStr.length);
                url += pnft(FMT_NORMAL, nameStr + " ".repeat(padding) + priceStr + " " + countStr);
            });

        url += pnft(FMT_NORMAL, SEP);
    }

    // Total
    url += pnft(FMT_CENTER,
        I18n.t("admin_local.total") + ": " + payable + " " + (currencyName || ""));

    url += pnft(FMT_NORMAL, SEP);

    // Order not yet finalized
    url += pnft(FMT_CENTER, I18n.t("admin_local.kiosk_order_not_finalized"));

    // Go to table to pay
    if (targetTableName)
        url += pnft(FMT_CENTER, String.format(I18n.t("admin_local.kiosk_go_to_table_to_pay"), targetTableName));

    // Feed paper so the slip can be torn off cleanly
    url += pnft(FMT_NORMAL, "");
    url += pnft(FMT_NORMAL, "");
    url += pnft(FMT_NORMAL, "");
    url += pnft(FMT_NORMAL, "");

    url += "Command=cnfr" + TAB + "\r\n";

    console.log(url);
    def.resolve(url);
};



/*
//url += "command=sar" + TAB + 1 + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + 0 + TAB + TAB + TAB + 1 + TAB + 1 + TAB + TAB + TAB + TAB + TAB + TAB + "P\r\n";
url += "command=sar" + TAB + 0 + TAB + moment().format("YYMMDD") + TAB + "F" + TAB + 1 + TAB + 1 + TAB + 0 + TAB + TAB + TAB + TAB + TAB + TAB + TAB + TAB + "\r\n";
//url += "Command=sbt" + TAB + TAB + TAB + TAB + TAB + TAB + TAB + TAB + 50 + TAB + 1 + TAB + TAB + "0," + "\r\n";

url += "Command=sbt" + TAB + TAB + "ccc" + TAB + TAB + TAB + TAB + TAB + a234["null"] + TAB + "00" + TAB + 50 + TAB + 1 + TAB + "0" + "\r\n";

url += "command=crr" + TAB + 1 + TAB + "06" + "\r\n";
*/
